import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DecimalInput } from '@/components/ui/decimal-input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'

/**
 * ProductTypeQuickCreateModal
 *
 * Quick modal for creating a new product type with pre-populated name.
 * Similar to customer quick-create pattern.
 *
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onConfirm - Callback with type data when confirmed
 * @param {string} defaultName - Pre-populated type name from CSV
 */
export default function ProductTypeQuickCreateModal({
  open,
  onClose,
  onConfirm,
  defaultName = '',
  initialValues = null
}) {
  const [name, setName] = useState('')
  const [referenceCode, setReferenceCode] = useState('')
  const [hasMultiplier, setHasMultiplier] = useState(false)
  const [priceMultiplier, setPriceMultiplier] = useState(1.0)

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      if (initialValues) {
        // Editing existing type configuration
        setName(initialValues.name || defaultName)
        setReferenceCode(initialValues.reference_code || '')
        const multiplier = initialValues.price_multiplier ?? 1.0
        setHasMultiplier(multiplier !== 1.0)
        setPriceMultiplier(multiplier)
      } else {
        // Creating new type
        setName(defaultName)
        setReferenceCode('')
        setHasMultiplier(false)
        setPriceMultiplier(1.0)
      }
    }
  }, [open, defaultName, initialValues])

  const handleConfirm = () => {
    if (!name?.trim()) {
      alert('Please provide a name for the product type')
      return
    }

    onConfirm({
      name: name.trim(),
      reference_code: referenceCode.trim() || null,
      price_multiplier: hasMultiplier ? priceMultiplier : 1.0
    })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="odin-modal-small">
        <DialogHeader>
          <DialogTitle>Create Product Type</DialogTitle>
          <DialogDescription>
            Confirm the details for this new product type.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label htmlFor="type-name">Type Name</Label>
              <Input
                id="type-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., A Class, B Class"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="type-ref-code">Item Ref</Label>
              <Input
                id="type-ref-code"
                value={referenceCode}
                onChange={(e) => setReferenceCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="AUTO"
                maxLength={6}
                className="uppercase"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="has-multiplier"
              checked={hasMultiplier}
              onCheckedChange={setHasMultiplier}
            />
            <Label htmlFor="has-multiplier" className="text-sm font-normal cursor-pointer">
              Add price multiplier
            </Label>
          </div>

          {hasMultiplier && (
            <div>
              <Label htmlFor="price-multiplier">
                Price Multiplier
                <span className="text-xs text-gray-500 ml-2 block mt-1">
                  1.60 = 60% premium | 1.00 = base | 0.75 = 25% discount | 0.00 = free
                </span>
              </Label>
              <DecimalInput
                id="price-multiplier"
                value={priceMultiplier}
                onChange={(value) => setPriceMultiplier(value || 1.0)}
                decimalPlaces={2}
                min={0}
                autoClearValue="1.00"
                placeholder="1.00"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
